import React from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { TopicUserRole } from '../graphql/generated/graphql';
import AdminListButton from '../user/AdminListButton';
import { useAllUsersMap } from '../user/useAllUsers';
import populateObjects from '../utils/populateObjects';
import * as styles from './TopicAccess.module.scss';
import { isRequestor } from './topicPolicyCheck';
import type { AllTopicsTreeListTopic } from './useAllTopics';
import { useAddTopicUser, useRequestAccess } from './useTopicUserMutations';
import TopicUserRow from './users/TopicUserRow';
import getTopicUsers from './utils/getTopicUsers';

interface TopicAccessProps {
  topic: AllTopicsTreeListTopic;
}

const TopicAccess = ({ topic }: TopicAccessProps) => {
  const { owners: topicOwnerIds } = getTopicUsers(topic);
  const user = useAppSelector((state) => state.session.user);
  const isAdmin = user.type === 'ADMIN';
  const isRequested = isRequestor(topic, user);
  const userMap = useAllUsersMap();
  const topicOwners = populateObjects(userMap, topicOwnerIds);

  const requestAccess = useRequestAccess();
  const addTopicUser = useAddTopicUser();
  const handleJoin = () => addTopicUser(topic, user);

  return (
    <div className={styles.card}>
      <div className={styles.cardContent}>
        {topic.privacy === 'PRIVATE' && <h2>This topic is private</h2>}
        {topic.privacy === 'SECRET' && <h2>This topic is secret</h2>}
        {topicOwners.length > 0 ? (
          <>
            <div className={styles.message}>
              When you request membership we will notify the following topic
              owners:
            </div>
            <div className={styles.userList}>
              {topicOwners.map((owner) => (
                <TopicUserRow
                  key={owner.id}
                  user={owner}
                  userRole={TopicUserRole.Owner}
                  hideMenu
                />
              ))}
            </div>
            {isRequested ? (
              <div className={styles.secondary}>
                Membership requested. You will receive an email when access is
                granted.
              </div>
            ) : (
              <Button
                size="large"
                variant="primary"
                onClick={() => requestAccess(topic.id)}
                className={styles.requestButton}
              >
                Request access
              </Button>
            )}
            {isAdmin && (
              <div className={styles.secondary}>
                {isRequested ? <>You can also</> : <>or</>}{' '}
                <Button onClick={handleJoin} variant="link">
                  join as site admin
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className={styles.message}>
              This topic has no owners to request access from.
              <br />
              {isAdmin ? (
                <>
                  Join as the site admin and assign an owner to this topic so
                  others can request access.
                </>
              ) : (
                <>
                  Ask{' '}
                  <AdminListButton variant="link" size="large">
                    an admin
                  </AdminListButton>{' '}
                  to assign an owner to this topic.
                </>
              )}
            </div>
            {isAdmin && (
              <Button
                size="large"
                variant="primary"
                onClick={handleJoin}
                className={styles.requestButton}
              >
                Join as admin
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TopicAccess;
